import { Button } from '@/components/ui/button';
import { Field, FieldContent, FieldLabel } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { Filters } from '@/components/search/filters';
import { DataTable } from '@/components/ui/data-table';
import { Pagination } from '@/components/pagination';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  CalendarIcon,
  LockSimpleIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';
import { format, sub } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useIsDemo } from '@/hooks/use-is-demo';
import { cn } from '@/utils/cn';
import _ from 'lodash';
import {
  keepPreviousData,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { useProjects } from '@/hooks/use-projects';
import { EventsService, QueriesService } from '@/api';
import { createFileRoute, Link, useRouteContext } from '@tanstack/react-router';
import { eventNamesOptions } from '@/hooks/query-options';
import { SavedQueries } from '@/components/search/saved-queries';
import { SaveQueryModal } from '@/components/search/save-query-modal';
import { SectionHeader } from '@/components/section-header';
import { useAppForm } from '@/hooks/use-app-form';
import { type ProjectEvent, type SearchFormValues } from '@/types';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import {
  EVENT_RESULT_FIELD_OPTIONS,
  EVENT_RESULT_FIELD_VALUES,
} from '@/components/search/field-options';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { SearchFormApi } from '@/components/search/form-types';
import { hasPermission } from '@/lib/permissions';

export const Route = createFileRoute('/_auth/_side-layout/queries')({
  component: Search,
  // Prefetch on intent (hover) so the data is warm by the time the route
  // mounts — matches the loader + useSuspenseQuery pattern used by the other
  // routes. Without this the page was the only one that fetched on render and
  // stalled behind the (now materialized) metadata-keys lookup.
  loader: ({ context }) => {
    const projectId = context.projectId;
    if (!projectId) return;
    context.queryClient.prefetchQuery(eventNamesOptions({ projectId }));
    context.queryClient.prefetchQuery({
      queryKey: ['event-properties', projectId],
      queryFn: () => EventsService.getEventProperties({ projectId }),
    });
    context.queryClient.prefetchQuery({
      queryKey: ['queries', projectId],
      queryFn: () => QueriesService.getQueries({ projectId }),
    });
  },
});

const LEGACY_FILTER_FIELD_MAP: Record<string, string> = {
  _id: 'event_id',
  _name: 'name',
  _project: 'project_name',
  _userId: 'user_id',
  _createdAt: 'time',
};

const LEGACY_RESULT_FIELD_MAP: Record<string, string> = {
  _id: 'id',
  _name: 'name',
  _project: 'project',
  _userId: 'userId',
  _createdAt: 'createdAt',
  _metadata: 'metadata',
  _location_country: 'location_country',
  _location_city: 'location_city',
  _location_region: 'location_region',
  _location_lon: 'location_lon',
  _location_lat: 'location_lat',
  _device: 'device',
  _os: 'os',
  _browser: 'browser',
  _screen: 'screen',
  _language: 'language',
  _referrer: 'referrer',
  _url: 'url',
  _hostname: 'hostname',
};

const normalizeFilterFieldName = (fieldName?: string) => {
  const value = fieldName?.trim() ?? '';
  return LEGACY_FILTER_FIELD_MAP[value] ?? value;
};

const normalizeResultFieldName = (fieldName: string) => {
  const trimmed = fieldName.trim();
  if (!trimmed) return '';
  return LEGACY_RESULT_FIELD_MAP[trimmed] ?? trimmed;
};

/** Handles both legacy string format and new string[] format for backward compatibility */
const normalizeSearchFormValues = (
  values: SearchFormValues,
): SearchFormValues => {
  const rawFields = values.fields;
  const fieldsArray: string[] =
    typeof rawFields === 'string'
      ? (rawFields as unknown as string)
          .split(',')
          .map((f) => normalizeResultFieldName(f))
          .filter(Boolean)
      : rawFields.map((f) => normalizeResultFieldName(f)).filter(Boolean);

  return {
    ...values,
    fields: fieldsArray,
    groups: (values.groups ?? []).map((group) => ({
      ...group,
      filters: (group.filters ?? []).map((filter) => ({
        ...filter,
        fieldName: normalizeFilterFieldName(filter.fieldName ?? undefined),
        operator: filter.operator ?? '=',
        value: filter.value ?? '',
      })),
    })),
  };
};

/** Converts form values to a format suitable for API storage (fields as comma-separated string) */
export const serializeSearchFormValues = (
  values: SearchFormValues,
): Record<string, unknown> => ({
  ...values,
  fields: values.fields.join(', '),
});

const sanitizeSearchFormValues = (
  values: SearchFormValues,
): SearchFormValues => ({
  ...values,
  groups: (values.groups ?? [])
    .map((group) => ({
      ...group,
      filters: (group.filters ?? []).filter((filter) => {
        const fieldName = filter.fieldName?.trim() ?? '';
        if (!fieldName) {
          return false;
        }

        if (
          filter.operator === 'IS NULL' ||
          filter.operator === 'IS NOT NULL'
        ) {
          return true;
        }

        return (filter.value?.toString().trim() ?? '').length > 0;
      }),
    }))
    .filter((group) => group.filters.length > 0),
});

function cleanUpFields(filters: SearchFormValues | null) {
  return (filters?.fields ?? [])
    .map((x) => normalizeResultFieldName(x))
    .filter(Boolean)
    .map((field) => {
      if (field === 'metadata') {
        return { id: field, label: field, stringify: true };
      }
      if (EVENT_RESULT_FIELD_VALUES.has(field)) {
        return { id: field, label: field };
      }
      if (field.startsWith('metadata.')) {
        return { id: field, label: field };
      }
      return { id: `metadata.${field}`, label: field };
    });
}

export const defaultValues = {
  userId: '',
  eventName: '',
  fields: ['id', 'createdAt', 'name', 'userId', 'metadata'],
  startDate: sub(new Date(), { months: 1 }).toISOString(),
  endDate: null as string | null,
  groups: [
    {
      filters: [
        {
          fieldName: '',
          operator: '=',
          value: '',
        },
      ],
    },
  ],
} satisfies SearchFormValues as SearchFormValues;

function Search() {
  const { projectId } = useProjects();
  const { userRole } = useRouteContext({ from: '/_auth' });
  const canEditProject = hasPermission(userRole, { project: ['update'] });
  const isDemo = useIsDemo();
  const [page, setPage] = useState(1);
  const [prevProjectId, setPrevProjectId] = useState(projectId);
  const [filters, setFilters] = useState<SearchFormValues | null>(null);
  const [selectedQuery, setSelectedQuery] = useState<{
    id: string;
    name: string;
    filters: SearchFormValues;
  } | null>(null);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [customFieldOpen, setCustomFieldOpen] = useState(false);
  const [customFields, setCustomFields] = useState<string[]>([]);

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      setFilters(sanitizeSearchFormValues(value));
      setPage(1);
    },
  });

  // Its own tiny form so the metadata-path input follows the field system
  // (Enter-to-submit, validation-ready); on submit it appends to the result
  // columns of the main search form above.
  const customFieldForm = useAppForm({
    defaultValues: { path: '' },
    onSubmit: ({ value }) => {
      const trimmed = (value.path ?? '').trim();
      if (!trimmed) return;
      const fieldValue =
        EVENT_RESULT_FIELD_VALUES.has(trimmed) ||
        trimmed.startsWith('metadata.')
          ? trimmed
          : `metadata.${trimmed}`;
      setCustomFields((prev) => [...new Set([...prev, fieldValue])]);
      const currentFields = form.getFieldValue('fields') ?? [];
      if (!currentFields.includes(fieldValue)) {
        form.setFieldValue('fields', [...currentFields, fieldValue]);
      }
      customFieldForm.reset();
      setCustomFieldOpen(false);
    },
  });

  // Reset during render (not an Effect) so a project switch can't query the new
  // project with the previous one's lingering filters/page.
  if (projectId !== prevProjectId) {
    setPrevProjectId(projectId);
    setPage(1);
    setFilters(null);
  }

  const { data: searchData } = useQuery({
    queryKey: ['events-search', projectId, filters, page],
    queryFn: () =>
      EventsService.searchEvents({
        page,
        projectId: projectId!,
        startDate: filters!.startDate ?? undefined,
        endDate: filters!.endDate ?? undefined,
        userId: filters!.userId,
        eventName: filters!.eventName,
        filterGroups: filters!.groups,
      }),
    enabled: !!filters && !!projectId,
    placeholderData: keepPreviousData,
  });

  const events = searchData?.events ?? [];
  const totalPages = searchData?.totalPages ?? 0;

  const { data } = useSuspenseQuery(
    eventNamesOptions({ projectId: projectId! }),
  );
  const { data: metadataFieldsData } = useSuspenseQuery({
    queryKey: ['event-properties', projectId],
    queryFn: async () =>
      EventsService.getEventProperties({ projectId: projectId! }),
  });

  const metadataFields = useMemo(
    () =>
      [...new Set((metadataFieldsData ?? []).filter(Boolean))]
        .map((field) => field.trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [metadataFieldsData],
  );

  const eventOptions = useMemo(
    () => [
      { value: '', label: 'Show all' },
      ...(data ?? []).map((event: { name: string }) => ({
        value: event.name,
        label: event.name,
      })),
    ],
    [data],
  );

  const fieldOptions = useMemo(() => {
    const standardOptions = EVENT_RESULT_FIELD_OPTIONS;
    const metadataOptions = metadataFields.map((f) => ({
      value: `metadata.${f}`,
      label: `Metadata: ${f}`,
    }));

    const knownValues = new Set([
      ...standardOptions.map((o) => o.value),
      ...metadataOptions.map((o) => o.value),
    ]);

    const customOptions = customFields
      .filter((v) => !knownValues.has(v))
      .map((v) => ({ value: v, label: v }));

    return [...standardOptions, ...metadataOptions, ...customOptions];
  }, [metadataFields, customFields]);

  const { data: queriesData } = useSuspenseQuery({
    queryKey: ['queries', projectId],
    queryFn: async () => QueriesService.getQueries({ projectId: projectId! }),
  });

  const [queries, setQueries] = useState<
    { id: string; name: string; filters: SearchFormValues }[]
  >([]);

  // User-editable local state (save/delete mutate it), so it's seeded from the
  // server query via an Effect rather than derived with useMemo.
  useEffect(() => {
    const normalizedQueries = (
      (queriesData?.queries as {
        id: string;
        name: string;
        filters: SearchFormValues;
      }[]) ?? []
    ).map((query) => ({
      ...query,
      filters: normalizeSearchFormValues(query.filters),
    }));

    setQueries(normalizedQueries);
  }, [queriesData]);

  const columns = useMemo<ColumnDef<ProjectEvent>[]>(
    () =>
      cleanUpFields(filters).map((key) => ({
        id: key.id,
        header: key.label,
        accessorFn: (row) => _.get(row, key.id),
        cell: ({ getValue }) =>
          key.stringify
            ? JSON.stringify(getValue() ?? {})
            : String(getValue() ?? ''),
      })),
    [filters],
  );

  return (
    <>
      <PageHeader
        title="Queries"
        description="Query and filter your event data with advanced search."
        isNew
      />
      <PageContent>
        <div className="space-y-4">
          {isDemo && (
            <div className="border-border bg-muted/40 flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <LockSimpleIcon className="text-muted-foreground mt-0.5 size-5 shrink-0" />
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold">
                    Query Builder is read-only in the demo
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Sign up to run and save custom queries across your own event
                    data.
                  </p>
                </div>
              </div>
              <Button asChild className="shrink-0">
                <Link to="/sign-up">Get started free</Link>
              </Button>
            </div>
          )}
          {/* In demo the builder is shown but inert + dimmed: prospects can see
            the feature's power without running queries against the shared demo
            project. */}
          <div
            className={cn(
              isDemo && 'pointer-events-none opacity-60 select-none',
            )}
            inert={isDemo || undefined}
          >
            <form
              onSubmit={(e: React.FormEvent) => {
                e.preventDefault();
                form.handleSubmit();
              }}
            >
              <form.AppForm card>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <SectionHeader
                    icon={<MagnifyingGlassIcon size={20} />}
                    title="Query Builder"
                    subTitle="Run and save custom queries to explore your event data."
                  />
                  <div className="flex flex-col items-start gap-2 sm:items-end">
                    <div className="flex flex-wrap items-center gap-2">
                      <Button type="submit">Run Query</Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          form.reset(defaultValues);
                          setSelectedQuery(null);
                          setFilters(null);
                          setPage(1);
                        }}
                      >
                        Reset
                      </Button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <SavedQueries
                        queries={queries}
                        selectedQueryId={selectedQuery?.id}
                        canDelete={canEditProject}
                        onDelete={(query) => {
                          const updatedQueries = queries.filter(
                            (q) => q.id !== query.id,
                          );
                          setQueries(updatedQueries);

                          if (selectedQuery?.id === query.id) {
                            setSelectedQuery(updatedQueries[0] ?? null);
                            form.reset(
                              updatedQueries[0]?.filters ?? defaultValues,
                            );
                          }

                          QueriesService.saveQueries({
                            projectId: projectId!,
                            queries: updatedQueries,
                          }).catch(console.error);
                        }}
                        onSelect={(query) => {
                          const normalized = normalizeSearchFormValues(
                            query.filters as SearchFormValues,
                          );
                          form.reset(normalized);
                          setSelectedQuery({
                            ...query,
                            filters: normalized,
                          });
                        }}
                      />
                      {canEditProject && (
                        <form.Subscribe selector={(s) => s.values}>
                          {(values) => (
                            <SaveQueryModal
                              formValues={values}
                              projectId={projectId!}
                              queries={queries}
                              setQueries={setQueries}
                            />
                          )}
                        </form.Subscribe>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <form.AppField name="userId">
                      {(field) => (
                        <field.Input
                          label="User ID"
                          placeholder="user@email.com"
                        />
                      )}
                    </form.AppField>
                    <form.AppField name="eventName">
                      {(field) => (
                        <field.Combobox
                          label="Event"
                          options={eventOptions}
                          placeholder="Show all"
                          searchPlaceholder="Search events..."
                        />
                      )}
                    </form.AppField>
                    <form.AppField name="startDate">
                      {(field) => {
                        const dateValue = field.state.value
                          ? new Date(field.state.value)
                          : null;
                        return (
                          <Field>
                            <FieldContent>
                              <FieldLabel>Start Date</FieldLabel>
                            </FieldContent>
                            <form.Subscribe selector={(s) => s.values.endDate}>
                              {(endDateValue) => (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      iconLeft={<CalendarIcon />}
                                      className={cn(
                                        'w-full justify-start text-left font-normal',
                                        !dateValue && 'text-muted-foreground',
                                      )}
                                    >
                                      {dateValue
                                        ? format(dateValue, 'PPP')
                                        : 'Pick a date'}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    className="w-auto p-0"
                                    align="start"
                                  >
                                    <Calendar
                                      mode="single"
                                      selected={dateValue ?? undefined}
                                      onSelect={(date) =>
                                        field.handleChange(
                                          date ? date.toISOString() : null,
                                        )
                                      }
                                      disabled={(date) =>
                                        endDateValue
                                          ? date > new Date(endDateValue)
                                          : false
                                      }
                                    />
                                  </PopoverContent>
                                </Popover>
                              )}
                            </form.Subscribe>
                          </Field>
                        );
                      }}
                    </form.AppField>
                    <form.AppField name="endDate">
                      {(field) => {
                        const dateValue = field.state.value
                          ? new Date(field.state.value)
                          : null;
                        return (
                          <Field>
                            <FieldContent>
                              <FieldLabel>End Date</FieldLabel>
                            </FieldContent>
                            <form.Subscribe
                              selector={(s) => s.values.startDate}
                            >
                              {(startDateValue) => (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      iconLeft={<CalendarIcon />}
                                      className={cn(
                                        'w-full justify-start text-left font-normal',
                                        !dateValue && 'text-muted-foreground',
                                      )}
                                    >
                                      {dateValue
                                        ? format(dateValue, 'PPP')
                                        : 'Pick a date'}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    className="w-auto p-0"
                                    align="start"
                                  >
                                    <Calendar
                                      mode="single"
                                      selected={dateValue ?? undefined}
                                      onSelect={(date) =>
                                        field.handleChange(
                                          date ? date.toISOString() : null,
                                        )
                                      }
                                      disabled={(date) =>
                                        startDateValue
                                          ? date < new Date(startDateValue)
                                          : false
                                      }
                                    />
                                  </PopoverContent>
                                </Popover>
                              )}
                            </form.Subscribe>
                          </Field>
                        );
                      }}
                    </form.AppField>
                  </div>
                  <form.AppField name="fields">
                    {(field) => (
                      <field.MultiSelect
                        label="Fields"
                        placeholder="Select columns to display..."
                        description="Choose which columns appear in the results table."
                        searchPlaceholder="Search fields..."
                        options={fieldOptions}
                        createNewLabel="Add custom metadata field"
                        onCreateNew={() => setCustomFieldOpen(true)}
                      />
                    )}
                  </form.AppField>
                </div>

                <div className="border-border/70 bg-background/40 rounded-lg border">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-4 py-3"
                    onClick={() => setFiltersExpanded(!filtersExpanded)}
                  >
                    <div className="flex items-center gap-2">
                      <div className="space-y-1">
                        <h3 className="text-left text-sm font-semibold">
                          Advanced Filters
                        </h3>
                        <p className="text-muted-foreground text-sm">
                          Add filters to narrow event results.
                        </p>
                      </div>
                      <form.Subscribe
                        selector={(s) => {
                          const groups = s.values.groups ?? [];
                          return groups.reduce(
                            (count, g) =>
                              count +
                              (g.filters?.filter((f) => f.fieldName?.trim())
                                .length ?? 0),
                            0,
                          );
                        }}
                      >
                        {(filterCount) =>
                          filterCount > 0 ? (
                            <Badge variant="neutral">
                              {filterCount} filter{filterCount !== 1 ? 's' : ''}
                            </Badge>
                          ) : null
                        }
                      </form.Subscribe>
                    </div>
                    {filtersExpanded ? (
                      <ChevronUpIcon className="text-muted-foreground h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="text-muted-foreground h-4 w-4" />
                    )}
                  </button>
                  {filtersExpanded && (
                    <div className="border-border/60 border-t p-4">
                      <Filters
                        form={form as unknown as SearchFormApi}
                        metadataFields={metadataFields}
                      />
                    </div>
                  )}
                </div>
              </form.AppForm>
            </form>
          </div>

          <Dialog open={customFieldOpen} onOpenChange={setCustomFieldOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Custom Metadata Field</DialogTitle>
                <DialogDescription>
                  Enter a metadata path to add as a result column (e.g. orderId
                  or order.summary.total).
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  customFieldForm.handleSubmit();
                }}
                className="space-y-4"
              >
                <customFieldForm.AppForm>
                  <customFieldForm.AppField name="path">
                    {(field) => (
                      <field.Input
                        label="Metadata Path"
                        placeholder="e.g. orderId or order.summary.total"
                      />
                    )}
                  </customFieldForm.AppField>
                </customFieldForm.AppForm>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setCustomFieldOpen(false);
                      customFieldForm.reset();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">Add Field</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {filters && (
            <div className="space-y-3">
              <DataTable
                columns={columns}
                data={events}
                manualPagination
                emptyMessage="No results found."
              />
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </PageContent>
    </>
  );
}
